import { useEffect, useRef, useState } from "react";
import { Bar, Pie } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import moment from "moment";
import { Icon } from "@iconify/react";
import { Autocomplete, MenuItem, TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import "./DashboardTSS.css";
import Loader from "../Components/Loader";
import formatSecondsToDuration from "../Helper/formatSecondsToDuration";

import {
  getAgentTSS,
  getBusinessUnits,
  getCampaignAgentTSS,
  getDashboardAgentStateByTLTSS,
  getDashboardCallTSS,
  getDashboardStatusCallTSS,
} from "../Store/Actions/DashboardAction";

const DashboardTSS = () => {
  const [agentData, setAgentData] = useState([]);
  const [agentList, setAgentList] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [callData, setCallData] = useState(null);
  const [campaignList, setCampaignList] = useState([]);

  const initialDisplayValues = {
    campaign: [],
    agent: [],
  };

  const [displayValues, setDisplayValues] = useState(initialDisplayValues);

  const initialParams = {
    businessUnitID: "",
    campaignID: "",
    leaderID: "",
    startDate: moment(),
    endDate: moment(),
  };

  const [filterParams, setFilterParams] = useState(initialParams);
  const [isFiltering, setIsFiltering] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [statusCallData, setStatusCallData] = useState([]);

  // Ref to always hold the latest filterParams
  const filterParamsRef = useRef(filterParams);

  const fetchData = (businessUnitID) => {
    const currentParams = filterParamsRef.current;

    getDashboardCallTSS({
      businessUnitID: businessUnitID
        ? businessUnitID
        : currentParams.businessUnitID,

      campaignID: currentParams.campaignID,
      leaderID: currentParams.leaderID,
      startDate: moment(currentParams.startDate).format("YYYY-MM-DD"),
      endDate: moment(currentParams.endDate).format("YYYY-MM-DD"),
    })
      .then((response) => {
        if (response.status) {
          setCallData(response.data);
          setIsFiltering(false);
        } else {
          console.log("error", response.error);
          setIsFiltering(false);
        }
      })
      .catch((err) => {
        console.log("err", err);
        setIsFiltering(false);
      });

    getDashboardAgentStateByTLTSS(currentParams.leaderID)
      .then((response) => {
        if (response.status) {
          setAgentData(response.data);
          setIsFiltering(false);
        } else {
          console.log("error", response.error);
          setIsFiltering(false);
        }
      })
      .catch((err) => {
        console.log("err", err);
        setIsFiltering(false);
      });

    getDashboardStatusCallTSS({
      businessUnitID: businessUnitID
        ? businessUnitID
        : currentParams.businessUnitID,

      campaignID: currentParams.campaignID,
      leaderID: currentParams.leaderID,
      startDate: moment(currentParams.startDate).format("YYYY-MM-DD"),
      endDate: moment(currentParams.endDate).format("YYYY-MM-DD"),
    })
      .then((response) => {
        if (response.status) {
          setStatusCallData(response.data);
          setIsFiltering(false);
        } else {
          console.log("error", response.error);
          setIsFiltering(false);
        }
      })
      .catch((err) => {
        console.log("err", err);
        setIsFiltering(false);
      });
  };

  // Keep ref updated when filterParams changes
  useEffect(() => {
    filterParamsRef.current = filterParams;
  }, [filterParams]);

  useEffect(() => {
    // Fetch data on first render
    fetchData();

    // Fetch data every ten seconds
    const interval = setInterval(() => {
      fetchData();
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    getBusinessUnits()
      .then((responseBusinessUnit) => {
        if (responseBusinessUnit.status) {
          setBusinessUnits(responseBusinessUnit.data);
          fetchData(responseBusinessUnit.data[0].businessunitid ?? null);

          setFilterParams({
            ...filterParams,
            businessUnitID: responseBusinessUnit.data[0].businessunitid ?? "",
          });

          getCampaignAgentTSS(
            responseBusinessUnit.data[0].businessunitid ?? null
          )
            .then((responseCampaign) => {
              if (responseCampaign.status) {
                setCampaignList(responseCampaign.data);
                setIsLoading(false);
              } else {
                console.log("error", responseCampaign.error);
                setIsLoading(false);
              }
            })
            .catch((err) => {
              console.log("err", err);
              setIsLoading(false);
            });

          getAgentTSS(responseBusinessUnit.data[0].businessunitid ?? null)
            .then((responseAgent) => {
              if (responseAgent.status) {
                setAgentList(responseAgent.data);
                setIsLoading(false);
              } else {
                console.log("error", responseAgent.error);
                setIsLoading(false);
              }
            })
            .catch((err) => {
              console.log("err", err);
              setIsLoading(false);
            });
        } else {
          console.log("error", responseBusinessUnit.error);
          setIsLoading(false);
        }
      })
      .catch((err) => {
        console.log("err", err);
        setIsLoading(false);
      });
  }, []);

  const optionsAgentStatus = {
    indexAxis: "y",
    maintainAspectRatio: false,

    plugins: {
      datalabels: {
        anchor: "end",
        align: "end",

        font: {
          size: 14,
          weight: 700,
        },

        color: "#393939",
        display: (context) => context.dataset.data[context.dataIndex] > 0,
      },

      legend: { position: "bottom" },

      title: {
        color: "#000",
        display: true,

        font: {
          size: 18,
          weight: 500,
        },

        text: `Agent Status Per TL ${moment(filterParams.startDate).format(
          "DD MMM YYYY"
        )} - ${moment(filterParams.endDate).format("DD MMM YYYY")}`,
      },
    },

    responsive: true,

    scales: {
      x: {
        grace: "20%",
      },
    },
  };

  const labels = agentData?.map((agent) => agent.agentcode);

  const dataAgentStatus = {
    labels,

    datasets: [
      {
        label: "READY",

        data: labels?.map((label) => {
          let currentAgent = agentData?.find(
            (agent) => agent.agentcode === label
          );

          return currentAgent.ready;
        }),

        backgroundColor: "#00FF00",
      },

      {
        label: "BUSY",

        data: labels?.map((label) => {
          let currentAgent = agentData?.find(
            (agent) => agent.agentcode === label
          );

          return currentAgent.busy;
        }),

        backgroundColor: "#FF0000",
      },

      {
        label: "ACW",

        data: labels?.map((label) => {
          let currentAgent = agentData?.find(
            (agent) => agent.agentcode === label
          );

          return currentAgent.acw;
        }),

        backgroundColor: "#FFE900",
      },

      {
        label: "AUX",

        data: labels?.map((label) => {
          let currentAgent = agentData?.find(
            (agent) => agent.agentcode === label
          );

          return currentAgent.aux;
        }),

        backgroundColor: "#EE7314",
      },
    ],
  };

  const optionsStatusCall = {
    maintainAspectRatio: false,
    responsive: true,

    plugins: {
      datalabels: {
        anchor: "end",
        align: "start",

        font: {
          size: 14,
          weight: 700,
        },

        color: "#393939",
        display: (context) => context.dataset.data[context.dataIndex] > 0,
      },

      legend: { position: "bottom" },

      title: {
        color: "#000",
        display: true,

        font: {
          size: 18,
          weight: 500,
        },

        text: "Status Call",
      },
    },
  };

  const dataStatusCall = {
    labels: statusCallData?.map((status) => status.statusgroup),

    datasets: [
      {
        data: statusCallData?.map((status) => status.value),

        backgroundColor: [
          "#00FF00",
          "#FF0000",
          "#FFE900",
          "#EE7314",
          "#00e1ff",
          "#f064e9",
          "#e68f0d",
          "#27cfb3",
        ],
      },
    ],
  };

  function handleSubmit(e) {
    e.preventDefault();
    setIsFiltering(true);
    fetchData();
  }

  function calculateRate(value, utilized) {
    if (utilized === 0) return 0;
    return ((value * 100) / utilized).toFixed(2);
  }

  return (
    <div className="dashboard-tss">
      <div className="list_header">
        <div className="list_headerLeft">
          <h1>Telemarketing</h1>
        </div>
      </div>

      <div className="containerDashboard">
        {isLoading ? (
          <Loader />
        ) : (
          <>
            <section className="filters">
              <form method="post" onSubmit={handleSubmit}>
                <div className="form-inputs">
                  <div className="filter-item">
                    <TextField
                      fullWidth
                      label="Business Unit"
                      select
                      value={filterParams.businessUnitID}
                      onChange={(event) =>
                        setFilterParams({
                          ...filterParams,
                          businessUnitID: event.target.value,
                        })
                      }
                    >
                      {businessUnits.map((unit) => (
                        <MenuItem
                          key={unit.businessunitid}
                          value={unit.businessunitid}
                          // onClick={() => {
                          //   setProductName(unit.ApplicationTypeName);
                          // }}
                        >
                          {unit.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </div>

                  <div className="filter-item">
                    <Autocomplete
                      filterSelectedOptions
                      multiple
                      options={campaignList}
                      value={displayValues.campaign}
                      getOptionLabel={(campaign) => campaign.campaignname}
                      onChange={(event, value) => {
                        setFilterParams({
                          ...filterParams,
                          campaignID: value?.length
                            ? value.map((val) => val.campaignid)
                            : "",
                        });

                        setDisplayValues({
                          ...displayValues,
                          campaign: value,
                        });
                      }}
                      renderInput={(params) => (
                        <TextField {...params} label="Campaign" />
                      )}
                    />
                  </div>

                  <button
                    className="filter-item clear-button"
                    type="button"
                    onClick={() => {
                      setDisplayValues(initialDisplayValues);
                      setFilterParams(initialParams);
                    }}
                  >
                    <Icon height="20" icon="ri:brush-3-line" />
                    Clear
                  </button>

                  <div className="filter-item">
                    <Autocomplete
                      filterSelectedOptions
                      multiple
                      options={agentList}
                      value={displayValues.agent}
                      getOptionLabel={(agent) => agent.agentcode}
                      onChange={(event, value) => {
                        setFilterParams({
                          ...filterParams,
                          leaderID: value?.length
                            ? value.map((val) => val.agentid)
                            : "",
                        });

                        setDisplayValues({
                          ...displayValues,
                          agent: value,
                        });
                      }}
                      renderInput={(params) => (
                        <TextField {...params} label="Leader" />
                      )}
                    />
                  </div>

                  <div className="filter-item date-range">
                    <DatePicker
                      disableFuture
                      label="Start Date"
                      maxDate={filterParams.endDate}
                      value={filterParams.startDate}
                      renderInput={(params) => <TextField {...params} />}
                      onChange={(newValue) =>
                        setFilterParams({
                          ...filterParams,
                          startDate: newValue,
                        })
                      }
                    />

                    <Icon color="#7E7E7E" icon="mdi:arrow-expand" width="24" />

                    <DatePicker
                      disableFuture
                      label="End Date"
                      minDate={filterParams.startDate}
                      value={filterParams.endDate}
                      renderInput={(params) => <TextField {...params} />}
                      onChange={(newValue) =>
                        setFilterParams({
                          ...filterParams,
                          endDate: newValue,
                        })
                      }
                    />
                  </div>

                  <button
                    className="filter-item filter-button"
                    disabled={isFiltering}
                    type="submit"
                  >
                    <Icon height="20" icon="fluent:script-20-filled" />
                    Filter
                  </button>
                </div>
              </form>
            </section>

            <section className="cards">
              <div className="item contacted">
                <Icon
                  className="icon"
                  height="40"
                  icon="uil:smile"
                  width="40"
                />

                <div>
                  <p className="title">Contacted</p>
                  <p className="value">{callData?.totalcontactedpayroll}</p>
                </div>
              </div>

              <div className="item contacted">
                <Icon
                  className="icon"
                  height="40"
                  icon="uil:smile"
                  width="40"
                />

                <div>
                  <p className="title">Contacted %</p>

                  <p className="value">
                    {calculateRate(
                      callData?.totalcontactedpayroll,
                      callData?.datautilizedpayroll
                    )}

                    %
                  </p>
                </div>
              </div>

              <div className="item uncontacted">
                <Icon className="icon" height="40" icon="uil:meh" width="40" />

                <div>
                  <p className="title">Uncontacted</p>
                  <p className="value">{callData?.totalnotcontactedpayroll}</p>
                </div>
              </div>

              <div className="item uncontacted">
                <Icon className="icon" height="40" icon="uil:meh" width="40" />

                <div>
                  <p className="title">Uncontacted %</p>

                  <p className="value">
                    {calculateRate(
                      callData?.totalnotcontactedpayroll,
                      callData?.datautilizedpayroll
                    )}

                    %
                  </p>
                </div>
              </div>

              <div className="item agree">
                <Icon className="icon" height="40" icon="uil:lock" width="40" />

                <div>
                  <p className="title">Total Agree</p>
                  <p className="value">{callData?.agreepayroll}</p>
                </div>
              </div>

              <div className="item agree">
                <Icon className="icon" height="40" icon="uil:lock" width="40" />

                <div>
                  <p className="title">Agree %</p>

                  <p className="value">
                    {calculateRate(
                      callData?.agreepayroll,
                      callData?.datautilizedpayroll
                    )}

                    %
                  </p>
                </div>
              </div>

              <div className="item call-initiated">
                <Icon
                  className="icon"
                  height="40"
                  icon="ic:baseline-phone"
                  width="40"
                />

                <div>
                  <p className="title">Call Initiated</p>
                  <p className="value">{callData?.totalattempt}</p>
                </div>
              </div>

              <div className="item call-initiated">
                <Icon className="icon" height="40" icon="fa:tasks" width="40" />

                <div>
                  <p className="title">Data Utilize</p>
                  <p className="value">{callData?.datautilizedpayroll}</p>
                </div>
              </div>
            </section>

            <section className="agents">
              <section className="item hourly-chart">
                <Bar
                  className="table"
                  options={optionsAgentStatus}
                  data={dataAgentStatus}
                  plugins={[ChartDataLabels]}
                />
              </section>

              <section className="item agents-chart">
                <div>
                  {statusCallData?.length > 0 ? (
                    <Pie
                      data={dataStatusCall}
                      options={optionsStatusCall}
                      plugins={[ChartDataLabels]}
                    />
                  ) : (
                    <p className="no-data">No Status Call Data Available</p>
                  )}
                </div>
              </section>

              <section className="agent-cards">
                <div className="agent-card top-card">
                  <p className="label">Online Agent</p>
                  <p className="value">{callData?.loginagent}</p>
                </div>

                <div className="agent-card top-card">
                  <p className="label">Total Agent</p>
                  <p className="value">{callData?.todayagent}</p>
                </div>

                <div className="agent-card middle-card">
                  <p className="label">Total Leadbase</p>
                  <p className="value">{callData?.totalleadbase}</p>
                </div>

                <div className="agent-card middle-card">
                  <p className="label">Avg Wait Time Agent</p>

                  <p className="value">
                    {formatSecondsToDuration(callData?.avgwaitduration)}
                  </p>
                </div>

                <div className="agent-card bottom-card">
                  <p className="label">Total Dialing</p>
                  <p className="value">{callData?.totaldialed}</p>
                </div>

                <div className="agent-card bottom-card">
                  <p className="label">Total Talk Duration</p>

                  <p className="value">
                    {formatSecondsToDuration(callData?.sumdur)}
                  </p>
                </div>
              </section>
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default DashboardTSS;
